import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface PropertyInquiry {
  id: string;
  property_id: string;
  full_name: string;
  email: string;
  phone: string;
  message: string;
  preferred_contact: 'email' | 'phone' | 'whatsapp';
  inquiry_type: 'viewing' | 'information' | 'offer';
  status: 'pending' | 'contacted' | 'completed' | 'cancelled';
  created_at: string;
  updated_at: string;
}

export const usePropertyInquiries = () => {
  return useQuery({
    queryKey: ['property-inquiries'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('property_inquiries')
        .select(`
          *,
          properties (
            id,
            title,
            slug,
            main_image_url
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });
};

export const useCreatePropertyInquiry = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inquiry: Omit<PropertyInquiry, 'id' | 'created_at' | 'updated_at' | 'status'>) => {
      const { data, error } = await supabase
        .from('property_inquiries')
        .insert([inquiry])
        .select()
        .single();

      if (error) throw error;

      // Send notification email to admin
      try {
        await supabase.functions.invoke('send-property-inquiry-notification', {
          body: {
            inquiry_id: data.id,
            property_id: inquiry.property_id,
            property_title: 'Property', // This will be enriched by the edge function
            customer_name: inquiry.full_name,
            customer_email: inquiry.email,
            customer_phone: inquiry.phone,
            message: inquiry.message,
            inquiry_type: inquiry.inquiry_type,
            preferred_contact: inquiry.preferred_contact,
          },
        });
      } catch (emailError) {
        console.error('Error sending notification email:', emailError);
        // Don't throw error, inquiry was created successfully
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-inquiries'] });
      toast({
        title: "Success",
        description: "Your inquiry has been sent successfully. We'll contact you soon!",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useUpdatePropertyInquiryStatus = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: PropertyInquiry['status'] }) => {
      const { data, error } = await supabase
        .from('property_inquiries')
        .update({ status })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-inquiries'] });
      toast({
        title: "Success",
        description: "Inquiry status updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useDeletePropertyInquiry = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('property_inquiries')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-inquiries'] });
      toast({
        title: "Success",
        description: "Inquiry deleted successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
